export const API_BASE_URL = import.meta.env.VITE_API_URL || (['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'http://localhost:3059/api' : 'https://crmaaafm.inplsoftwares.com/api');

export const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = {
        ...options.headers
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
        ...options,
        headers,
        credentials: 'include'
    };

    const response = await fetch(url, config);

    if (response.status === 401 && !url.includes('/logout')) {
        window.dispatchEvent(new Event('auth:unauthorized'));
    }

    return response;
};

export const authService = {
    async signup(username, email, password, role) {
        const response = await fetchWithAuth(`${API_BASE_URL}/signup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, email, password, role })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Signup failed');
        if (data.data?.token) localStorage.setItem('token', data.data.token);
        return data;
    },

    async login(email, password) {
        const response = await fetchWithAuth(`${API_BASE_URL}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Login failed');
        if (data.data?.token) {
            localStorage.setItem('token', data.data.token);
            // Notify hooks (e.g. useAppSettings) that auth is now available
            window.dispatchEvent(new Event('auth:login'));
        }
        return data;
    },

    async logout() {
        const token = localStorage.getItem('token');
        const response = await fetchWithAuth(`${API_BASE_URL}/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        localStorage.removeItem('token');
        return response.json();
    },

    async changePassword(currentPassword, newPassword) {
        const response = await fetchWithAuth(`${API_BASE_URL}/change-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentPassword, newPassword })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to change password');
        return data;
    },

    getToken() {
        return localStorage.getItem('token');
    },

    isAuthenticated() {
        return !!localStorage.getItem('token');
    }
};

export const contactService = {
    async getAll(paramsObj = {}) {
        const { page = 1, limit = 50, search = '', source = '', from = '', to = '', approvalStatus = '' } = paramsObj;
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit.toString());
        if (search) params.append('search', search);
        if (source && source !== 'All') params.append('source', source);
        if (approvalStatus && approvalStatus !== 'All') params.append('approvalStatus', approvalStatus);
        if (from) params.append('from', from);
        if (to) params.append('to', to);

        const url = `${API_BASE_URL}/profiles${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetchWithAuth(url, {
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch profiles');
        return {
            data: data.data || [],
            pagination: data.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 }
        };
    },

    async approve(id, { assignedToId, remarks } = {}) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}/approve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ assignedToId, remarks })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to approve profile');
        return data.data;
    },

    async reject(id, remarks) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ remarks })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to reject profile');
        return data.data;
    },

    async resubmit(id, updateData = {}) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}/resubmit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to resubmit profile');
        return data.data;
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch profile');
        return data.data;
    },

    async create(contactData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contactData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create profile');
        return data.data;
    },

    async update(id, contactData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contactData)
        });
        const data = await response.json();
        if (!response.ok) {
            // Keep the body on the error. A 409 here names the profile that
            // already owns the number, which is the useful half of the reply —
            // throwing only the message discards it.
            const err = new Error(data.message || 'Failed to update profile');
            err.status = response.status;
            err.payload = data;
            throw err;
        }
        return data.data;
    },

    async addNote(id, payloadOrContent) {
        // Accept either a plain string (simple note) or a full {content, type, userId} object
        const body = (typeof payloadOrContent === 'object' && payloadOrContent !== null && 'content' in payloadOrContent)
            ? payloadOrContent
            : { content: payloadOrContent, type: 'NOTE' };
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}/notes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to add note');
        return data.data;
    },

    async logCall(id, callData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}/call`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(callData)
        });
        const data = await response.json();
        if (!response.ok) {
            console.error('[contactService.logCall] Server error:', data);
            throw new Error(data.message || 'Failed to log call');
        }
        return data.data;
    },

    async getProfileTimeline(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}/timeline`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch timeline');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete profile');
        return data;
    },

    async deleteAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete all profiles');
        return data;
    },

    async import(contactsData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/profiles/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ profiles: contactsData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import profiles');
        return data;
    }
};

export const sourcesService = {
    async getAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch sources');
        return data.data || [];
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch source');
        return data.data;
    },

    async create(name) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create source');
        return data.data;
    },

    async update(id, name) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update source');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete source');
        return data;
    },

    async getSourceLeads(id, params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/sources/${id}/prequalifications?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch source prequalifications');
        return data;
    }
};

export const servicesService = {
    async getAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/services`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch services');
        return data.data || [];
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/services/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch service');
        return data.data;
    },

    async create(name) {
        const response = await fetchWithAuth(`${API_BASE_URL}/services`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create service');
        return data.data;
    },

    async update(id, name) {
        const response = await fetchWithAuth(`${API_BASE_URL}/services/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update service');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/services/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete service');
        return data;
    }
};

export const citiesService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/cities?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch cities');
        return data.data || [];
    },

    async create(cityInput, country = 'PK') {
        const payload = typeof cityInput === 'object' ? cityInput : { name: cityInput, country };
        const response = await fetchWithAuth(`${API_BASE_URL}/cities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create city');
        return data.data;
    },

    async update(id, name, country) {
        const response = await fetchWithAuth(`${API_BASE_URL}/cities/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, country })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update city');
        return data.data;
    },

    async toggleStatus(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/cities/${id}/toggle`, {
            method: 'PATCH'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to toggle city status');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/cities/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete city');
        return data;
    }
};

export const prequalificationService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch prequalifications');
        return data;
    },

    async getStats() {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/stats`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch prequalifications stats');
        return data.data;
    },

    async getAnalytics(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/analytics?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch prequalifications analytics');
        return data.data;
    },

    async getCalendarTentative() {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/calendar-tentative`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch tentative prequalifications');
        return data.data || [];
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch prequalification');
        return data.data;
    },

    async create(leadData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create prequalification');
        return data.data;
    },

    async update(id, leadData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update prequalification');
        return data.data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete prequalification');
        return data;
    },

    async deleteAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/all`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete all prequalifications');
        return data;
    },

    async import(leadsData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prequalifications: leadsData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import prequalifications');
        return data;
    },

    async addNote(id, noteData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}/timeline`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(noteData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to add note');
        return data.data;
    },

    async getTimeline(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}/timeline`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch timeline');
        return data.data;
    },

    async addPayment(id, paymentData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}/payments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(paymentData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to add payment');
        return data; // Return full response { status, message, data }
    },

    async deletePayment(id, paymentId) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}/payments/${paymentId}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete payment');
        return data;
    },

    async exportContracts() {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/export/contracts`);
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to export contracts data');
        }
        return response; // Return response to get blob in the component
    },

    // Backward-compat alias
    async exportFinance() {
        return this.exportContracts();
    },

    async logCall(id, callData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/prequalifications/${id}/call-log`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(callData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to log call');
        return data.data;
    }
};

export const userService = {
    async getAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/users`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch users');
        return data.data;
    },

    async invite(userData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to invite user');
        return data; // returns full response { status, message, data: { user, tempPassword, resetLink } }
    },

    async update(userData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update user');
        return data; // returns full response { status, message, data: { ... } }
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete user');
        return data;
    },

    async resetPassword(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}/reset-password`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to reset password');
        return data; // returns { status, data: { tempPassword } }
    },

    async changePassword(id, newPassword) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}/password`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newPassword })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to change password');
        return data;
    },

    async toggleActive(id, is_active) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/${id}/toggle-active`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_active })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update user status');
        return data;
    },

    async import(usersData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/users/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ users: usersData })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import users');
        return data;
    }
};

export const settingsService = {
    async get(key) {
        const response = await fetchWithAuth(`${API_BASE_URL}/settings/${key}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch settings');
        return data.data;
    },

    async update(key, value) {
        const response = await fetchWithAuth(`${API_BASE_URL}/settings/${key}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update settings');
        return data.data;
    }
};

export const maintenanceService = {
    async resetDatabase() {
        const response = await fetchWithAuth(`${API_BASE_URL}/maintenance/reset`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to reset database');
        return data;
    },

    async migrateContacts() {
        const response = await fetchWithAuth(`${API_BASE_URL}/maintenance/migrate-profiles`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to migrate profiles');
        return data;
    },

    async consolidateSources() {
        const response = await fetchWithAuth(`${API_BASE_URL}/maintenance/consolidate-sources`, {
            method: 'POST'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to consolidate sources');
        return data;
    }
};

export const notificationsService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch notifications');
        return data; // returns { data, totalCount }
    },

    async markAsRead(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/${id}/read`, {
            method: 'PATCH'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to mark notification as read');
        return data;
    },

    async markAllAsRead(userData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/read-all`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to mark all as read');
        return data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/notifications/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete notification');
        return data;
    }
};

export const auditService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/audit-logs?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch audit logs');
        return data; // returns { status, data, meta }
    }
};

export const bookingService = {
    async getAll(params = {}) {
        return { status: true, data: [] };
    },
    async getById(id) {
        return null;
    }
};

export const conversationService = {
    async getAll(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetchWithAuth(`${API_BASE_URL}/conversations?${query}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch conversations');
        return data; // returns { status, data, meta }
    },
    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/conversations/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch conversation');
        return data.data;
    }
};



export const tendersService = {
    async getAll(paramsObj = {}) {
        const params = new URLSearchParams();
        Object.entries(paramsObj).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== '') params.append(key, val);
        });
        const url = `${API_BASE_URL}/tenders${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetchWithAuth(url);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch tenders');
        return data;
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch tender');
        return data;
    },

    async create(tenderData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tenderData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create tender');
        return data;
    },

    async update(id, tenderData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tenderData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update tender');
        return data;
    },

    async delete(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete tender');
        return data;
    },

    async addNote(id, noteData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/${id}/timeline`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(noteData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to add note');
        return data;
    },

    async getTimeline(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/${id}/timeline`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch timeline');
        return data;
    },

    async import(tendersData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tendersData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import tenders');
        return data;
    },

    async deleteAll() {
        const response = await fetchWithAuth(`${API_BASE_URL}/tenders/all`, {
            method: 'DELETE'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to delete all tenders');
        return data;
    }
};



export const contractsService = {
    async getAll(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append('status', params.status);
        if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
        if (params.search) queryParams.append('search', params.search);
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.dateAxis) queryParams.append('dateAxis', params.dateAxis);

        const response = await fetchWithAuth(`${API_BASE_URL}/contracts?${queryParams.toString()}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch contracts');
        return data;
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch contract');
        return data;
    },

    async create(contractData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contractData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create contract');
        return data;
    },

    async update(id, contractData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contractData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update contract');
        return data;
    },

    async exportCsv() {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts/export/csv`);
        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.message || 'Failed to export contracts data');
        }
        return response; // Return response to get blob in the component
    },

    async extend(id, payload) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts/${id}/extend`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to extend contract');
        return data;
    },

    async terminate(id, payload) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts/${id}/terminate`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to terminate contract');
        return data;
    },

    async import(contractsData) {
        const response = await fetchWithAuth(`${API_BASE_URL}/contracts/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contractsData)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to import contracts');
        return data;
    }
};

export const approvalService = {
    async getAll(paramsObj = {}) {
        const params = new URLSearchParams();
        Object.entries(paramsObj).forEach(([key, val]) => {
            if (val !== undefined && val !== null && val !== '') params.append(key, val);
        });
        const url = `${API_BASE_URL}/approvals${params.toString() ? '?' + params.toString() : ''}`;
        const response = await fetchWithAuth(url);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch approvals');
        return data;
    },

    async getCounts() {
        const response = await fetchWithAuth(`${API_BASE_URL}/approvals/counts`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch approval counts');
        return data;
    },

    async getById(id) {
        const response = await fetchWithAuth(`${API_BASE_URL}/approvals/${id}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch approval details');
        return data;
    },

    async approve(id, payload = {}) {
        const response = await fetchWithAuth(`${API_BASE_URL}/approvals/${id}/approve`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to approve request');
        return data;
    },

    async reject(id, payload = {}) {
        const response = await fetchWithAuth(`${API_BASE_URL}/approvals/${id}/reject`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to reject request');
        return data;
    },

    async submit(payload) {
        const response = await fetchWithAuth(`${API_BASE_URL}/approvals/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to submit for approval');
        return data;
    },

    // Fetch the latest ApprovalRequest for a given entity (entityType = 'PROFILE'|'PREQUALIFICATION'|'TENDER', entityId = number)
    async getByEntity(entityType, entityId) {
        if (!entityId) return null;
        const response = await fetchWithAuth(`${API_BASE_URL}/approvals/by-entity/${entityType}/${entityId}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to fetch approval');
        return data.data || null;
    }
};

export default authService;

